import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import "./ProductV2.css";
import "./lovable-catalog-theme.css";
import "./lovable-catalog-components.css";
import {
  getProductList,
  getSubgroupProductList,
  createSubgroupProduct,
} from "../../apis/Product";
import Service from "../Service";
import { ContainerBody } from "../../App";
import { useTranslation } from "react-i18next";
import { getBusinessDetail } from "../../apis/Business/Business";
import { useUserAccess } from "../../components/WriteAccessContext";
import { useInvalidateQuery } from "../../components/InvalidateQueryContext";
import { ProductApiKey } from "../../apis/ApiKey/ProductApiKey";
import { YearTabContext } from "../../components/Skeleton";
import FilterSearchProduct from "./FilterSearchProduct";
import ProductCatalog, { CatalogObjectTypeTiles } from "./ProductCatalog";
import ProductDetail from "../ProductDetail";
import { ProductCatalogTabContext } from "./ProductCatalogTabContext";
import { EmbeddedProductRouteProviders } from "./EmbeddedProductRouteProviders";
import { cn } from "../BusinessDash/lib/utils";
import { LayoutGrid, Wrench, FolderKanban, Clock, X } from "lucide-react";

const ProductV2 = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const yearTab = useContext(YearTabContext);

  const yearFromUrl = urlParams.get("year");
  const year = yearFromUrl || yearTab || "";
  const assessmentType = urlParams.get("assessment_type") || "";

  const [products, setProducts] = useState([]);
  const [productMenu, setProductMenu] = useState("product");
  const [chosenTab, setChosenTab] = useState("normal");
  const [subgroups, setSubgroups] = useState([]);
  const [chooseAlphabet] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [shared, setShared] = useState({});
  const [sharedSubgroups, setSharedSubgroups] = useState({});
  const [searchbar] = useState("");
  const { t } = useTranslation();
  const [activeMainBusinessName, setActiveMainBusinessName] = useState(null);
  const [writeAccess, setWriteAccess] = useState(false);
  const { setUserAccessType, setIsShare, userAccess } = useUserAccess();
  const { invalidateKey, resetKey } = useInvalidateQuery();
  const { duplicateProduct, createProduct } = ProductApiKey;
  const [recentSubgroups, setRecentSubgroups] = useState([]);
  /** Lovable-style sub-page tab: catalog vs in-app product calculation */
  const [pageTab, setPageTab] = useState("catalog");
  /** Browser-style open products from catalog (PCF = amber, LCA = emerald) */
  const [openProductTabs, setOpenProductTabs] = useState([]);
  const [activeProductTabId, setActiveProductTabId] = useState(null);
  const tabScrollYRef = useRef({ __catalog__: 0 });

  const goCatalogFromEmbed = useCallback(() => {
    if (activeProductTabId) {
      tabScrollYRef.current[activeProductTabId] = window.scrollY;
    }
    setPageTab("catalog");
    setActiveProductTabId(null);
  }, [activeProductTabId]);

  const catalogTabContextValue = useMemo(
    () => ({ goCatalog: goCatalogFromEmbed }),
    [goCatalogFromEmbed]
  );

  const handleOpenProductInTab = useCallback(
    (payload) => {
      const id = String(payload.id);
      if (pageTab === "catalog") {
        tabScrollYRef.current.__catalog__ = window.scrollY;
      } else if (pageTab === "calculation" && activeProductTabId) {
        tabScrollYRef.current[activeProductTabId] = window.scrollY;
      }
      setOpenProductTabs((prev) => {
        if (prev.some((t) => t.id === id)) return prev;
        return [
          ...prev,
          {
            id,
            name: payload.name,
            method: payload.method === "lca" ? "lca" : "pcf",
            business: payload.business || "",
            assessmentType: payload.assessmentType || "PCF",
          },
        ];
      });
      setActiveProductTabId(id);
      setPageTab("calculation");
    },
    [pageTab, activeProductTabId]
  );

  const selectCatalogPageTab = useCallback(() => {
    if (pageTab === "calculation" && activeProductTabId) {
      tabScrollYRef.current[activeProductTabId] = window.scrollY;
    }
    setPageTab("catalog");
    setActiveProductTabId(null);
  }, [pageTab, activeProductTabId]);

  const selectProductPageTab = useCallback(
    (id) => {
      if (pageTab === "catalog") {
        tabScrollYRef.current.__catalog__ = window.scrollY;
      } else if (pageTab === "calculation" && activeProductTabId && activeProductTabId !== id) {
        tabScrollYRef.current[activeProductTabId] = window.scrollY;
      }
      setActiveProductTabId(id);
      setPageTab("calculation");
    },
    [pageTab, activeProductTabId]
  );

  const closeProductTab = useCallback(
    (e, id) => {
      e?.stopPropagation();
      setOpenProductTabs((prev) => {
        const next = prev.filter((t) => t.id !== id);
        delete tabScrollYRef.current[id];
        if (activeProductTabId !== id) return next;

        if (next.length === 0) {
          setPageTab("catalog");
          setActiveProductTabId(null);
        } else {
          const idx = prev.findIndex((t) => t.id === id);
          const pick = next[Math.max(0, idx - 1)] ?? next[0];
          setActiveProductTabId(pick.id);
        }
        return next;
      });
    },
    [activeProductTabId]
  );

  useLayoutEffect(() => {
    if (pageTab === "catalog") {
      window.scrollTo(0, tabScrollYRef.current.__catalog__ ?? 0);
    } else if (pageTab === "calculation" && activeProductTabId) {
      window.scrollTo(0, tabScrollYRef.current[activeProductTabId] ?? 0);
    }
  }, [pageTab, activeProductTabId]);

  const buildTabInitialPath = useCallback(
    (tab) => {
      const qs = new URLSearchParams();
      if (tab.business) qs.set("business", tab.business);
      const y = year || yearTab;
      if (y) qs.set("year", y);
      if (tab.assessmentType) qs.set("assessment_type", tab.assessmentType);
      const q = qs.toString();
      return `/product/${tab.id}${q ? `?${q}` : ""}`;
    },
    [year, yearTab]
  );

  // Load recent subgroups from localStorage
  useEffect(() => {
    if (year || yearTab) {
      const stored = localStorage.getItem("recentSubgroups");
      if (stored) {
        const all = JSON.parse(stored);
        setRecentSubgroups(
          all.filter((item) => item.year === (year || yearTab))
        );
      }
    }
  }, [year, yearTab]);

  useEffect(() => {
    if (chosenTab === "normal") {
      setUserAccessType("write");
    }
  }, [chosenTab, setUserAccessType]);

  /**
   * Map dialog entries to API `variation_categories`: each string is a dimension **name**
   * (e.g. Color, Size), not a value under a single "Variation" bucket. Values (e.g. black,
   * white) are added later on products; subgroup stores empty `values` until then.
   */
  const buildSubgroupVariationCategories = (dimensionNames) => {
    if (!Array.isArray(dimensionNames) || dimensionNames.length === 0) {
      return undefined;
    }
    const seen = new Set();
    const categories = [];
    for (const raw of dimensionNames) {
      const name = String(raw).trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      categories.push({ name, values: [] });
    }
    if (categories.length === 0) return undefined;
    return categories;
  };

  const handleSaveCustomSubCatNew = (name, dimensionNames = []) => {
    if (!name.trim()) return;
    const variation_categories = buildSubgroupVariationCategories(dimensionNames);
    const yearForPayload = year || yearTab || "";
    if (chosenTab === "normal") {
      createSubgroupProduct({
        name,
        variation_categories,
        year: yearForPayload,
        callback: () => {
          getSubgroupProductList({
            setSubgroups,
            setSharedSubgroups,
            yearData: year || yearTab,
          });
        },
      });
    } else {
      const business = sharedSubgroups[chosenTab].data[0]?.business;
      const sharedAdmin = sharedSubgroups[chosenTab].id;
      createSubgroupProduct({
        name,
        variation_categories,
        year: yearForPayload,
        is_shared: true,
        shared_business: business,
        shared_admin_id: sharedAdmin,
        callback: () => {
          getSubgroupProductList({
            setSubgroups,
            setSharedSubgroups,
            yearData: year || yearTab,
          });
        },
      });
    }
  };

  const handleToggleActive = (mainBusinessName) => {
    setActiveMainBusinessName(mainBusinessName);
  };

  useEffect(() => {
    if (year || yearTab) {
      getProductList({
        setProducts,
        setShared,
        setWriteAccess,
        chosenYear: year || yearTab,
        assessmentType,
      });
      getSubgroupProductList({
        setSubgroups,
        setSharedSubgroups,
        yearData: year || yearTab,
      });
      getBusinessDetail({
        id: localStorage.getItem("id_business"),
        setName: setBusinessName,
      });
    }
  }, [year, yearTab, assessmentType]);

  // Merge subgroup products into main products list
  useEffect(() => {
    if (subgroups.length > 0) {
      const subgroupProducts = [];
      subgroups.forEach((subgroup) => {
        if (subgroup.products && Array.isArray(subgroup.products)) {
          subgroup.products.forEach((product) => {
            if (product.is_product) {
              const prodAssessmentType = product.assessment_type || "PCF";
              const matchesFilter =
                !assessmentType || prodAssessmentType === assessmentType;
              if (matchesFilter) {
                const productYear = product.year;
                const targetYear = year || yearTab;
                if (targetYear && productYear === targetYear) {
                  subgroupProducts.push(product);
                }
              }
            }
          });
        }
      });

      setProducts((prevProducts) => {
        const existingIds = new Set(prevProducts.map((p) => p.id));
        const newProducts = subgroupProducts.filter(
          (p) => !existingIds.has(p.id)
        );
        return [...prevProducts, ...newProducts];
      });
    }
  }, [subgroups, assessmentType, year, yearTab]);

  useEffect(() => {
    if (invalidateKey === createProduct || invalidateKey === duplicateProduct) {
      getProductList({
        setProducts,
        setShared,
        setWriteAccess,
        chosenYear: year || yearTab,
        assessmentType,
      });
    }
    resetKey();
  }, [
    createProduct,
    duplicateProduct,
    invalidateKey,
    resetKey,
    assessmentType,
    year,
    yearTab,
  ]);

  const trackRecentSubgroup = (subgroup) => {
    const stored = localStorage.getItem("recentSubgroups");
    let recent = stored ? JSON.parse(stored) : [];
    recent = [
      { id: subgroup.id, name: subgroup.name, year: year ? year : yearTab },
      ...recent.filter((sg) => sg.id !== subgroup.id),
    ].slice(0, 8);
    localStorage.setItem("recentSubgroups", JSON.stringify(recent));
    setRecentSubgroups(
      recent.filter((item) => item.year === (year || yearTab))
    );
  };

  const catalogNavTiles = useMemo(
    () => [
      { value: "product", label: t("PRODUCT"), icon: LayoutGrid },
      { value: "service", label: t("SERVICE"), icon: Wrench },
      { value: "project", label: t("PROJECT"), icon: FolderKanban },
    ],
    [t]
  );

  const catalogSubgroups =
    chosenTab === "normal"
      ? subgroups
      : Array.isArray(sharedSubgroups[chosenTab]?.data)
        ? sharedSubgroups[chosenTab].data
        : [];
  const catalogProducts =
    chosenTab === "normal" ? products : shared[chosenTab] || [];

  return (
    <ContainerBody>
      <div className="lov-catalog-root bg-background text-foreground min-h-full w-full">
      <div className="pv2-page">
        {/* ── Business / Organisation Tabs ── */}
        <div className="pv2-org-tabs">
          <button
            className={`pv2-org-tab${activeMainBusinessName === null ? " active" : ""}`}
            onClick={() => {
              setChosenTab("normal");
              handleToggleActive(null);
              setIsShare(false);
              setUserAccessType("write");
            }}
          >
            {businessName ? businessName : t("Branches")}
          </button>
          {shared &&
            Object.keys(shared).map((mainBusinessName) => (
              <button
                key={mainBusinessName}
                className={`pv2-org-tab${activeMainBusinessName === mainBusinessName ? " active" : ""}`}
                onClick={() => {
                  setChosenTab(mainBusinessName);
                  setUserAccessType(writeAccess[mainBusinessName][0].is_write);
                  setIsShare(true);
                  handleToggleActive(mainBusinessName);
                }}
              >
                {mainBusinessName}
              </button>
            ))}
        </div>

        {/* ── Product: browser-style tab bar + catalog / in-app ProductDetail ── */}
        {productMenu === "product" && (
          <>
            <div
              className="border-b border-border bg-card sticky z-[40] shadow-sm"
              style={{ top: "58px" }}
            >
              <div className="flex gap-0 items-end overflow-x-auto max-w-[1920px] mx-auto px-2">
                <button
                  type="button"
                  onClick={selectCatalogPageTab}
                  className={cn(
                    "relative pl-3 pr-5 py-3 text-sm font-medium transition-colors focus:outline-none shrink-0",
                    pageTab === "catalog"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t("PRODUCT_CATALOG", { defaultValue: "Product Catalog" })}
                  {pageTab === "catalog" && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
                  )}
                </button>

                {openProductTabs.map((tab) => {
                  const isActive =
                    pageTab === "calculation" && activeProductTabId === tab.id;
                  const isPCF = tab.method === "pcf";
                  const tabColor = isPCF
                    ? "border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                    : "border-emerald-600 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400";
                  const activeUnderline = isPCF
                    ? "bg-amber-500"
                    : "bg-emerald-600";
                  return (
                    <div
                      key={tab.id}
                      className={cn(
                        "relative flex items-center gap-1.5 pl-4 pr-2 py-3 text-sm font-medium transition-colors shrink-0 cursor-pointer group",
                        isActive
                          ? tabColor
                          : isPCF
                            ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20"
                            : "bg-emerald-600/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-600/20"
                      )}
                    >
                      <span
                        role="button"
                        tabIndex={0}
                        className="flex items-center gap-1.5 min-w-0"
                        onClick={() => selectProductPageTab(tab.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            selectProductPageTab(tab.id);
                          }
                        }}
                      >
                        <span
                          className={cn(
                            "inline-block w-1.5 h-1.5 rounded-full shrink-0",
                            isPCF ? "bg-amber-500" : "bg-emerald-600"
                          )}
                        />
                        <span className="truncate max-w-[200px]">{tab.name}</span>
                      </span>
                      <button
                        type="button"
                        onClick={(e) => closeProductTab(e, tab.id)}
                        className="ml-1 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                        aria-label={t("CLOSE")}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      {isActive && (
                        <span
                          className={cn(
                            "absolute bottom-0 left-0 right-0 h-0.5 rounded-t",
                            activeUnderline
                          )}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {pageTab === "catalog" && (
              <div className="pv2-lov-catalog-frame">
                <div className="pv2-content">
                  <ProductCatalog
                    subgroups={catalogSubgroups}
                    products={catalogProducts}
                    year={year || yearTab}
                    assessmentType={assessmentType}
                    businessFallbackId={localStorage.getItem("id_business") || ""}
                    userAccess={userAccess}
                    showObjectTypeTiles={false}
                    pageTypeTiles={{
                      selected: productMenu,
                      onChange: setProductMenu,
                      options: catalogNavTiles,
                    }}
                    onAddCategory={(name, dimensionNames) =>
                      handleSaveCustomSubCatNew(name, dimensionNames)
                    }
                    onOpenProductInTab={handleOpenProductInTab}
                    trackRecentSubgroup={trackRecentSubgroup}
                    recentDisplayName={recentSubgroups[0]?.name}
                  />
                </div>
              </div>
            )}

            {openProductTabs.length > 0 && (
              <ProductCatalogTabContext.Provider value={catalogTabContextValue}>
                {openProductTabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={
                      pageTab === "calculation" && activeProductTabId === tab.id
                        ? "block min-h-[50vh]"
                        : "hidden"
                    }
                    aria-hidden={
                      pageTab !== "calculation" ||
                      activeProductTabId !== tab.id
                    }
                  >
                    <EmbeddedProductRouteProviders
                      fullPath={buildTabInitialPath(tab)}
                    >
                      <ProductDetail />
                    </EmbeddedProductRouteProviders>
                  </div>
                ))}
              </ProductCatalogTabContext.Provider>
            )}
          </>
        )}

        {/* ── Service / Project: type tiles + search (no product tab bar) ── */}
        {productMenu !== "product" && (
          <div className="pv2-lov-catalog-frame">
            <div className="pv2-lov-type-tiles mb-4">
              <CatalogObjectTypeTiles
                selected={productMenu}
                onChange={setProductMenu}
                options={catalogNavTiles}
                variant="compact"
              />
            </div>

            <div className="pv2-search-section">
              {chosenTab === "normal" ? (
                <FilterSearchProduct
                  subgroups={subgroups}
                  products={products}
                  year={year ? year : yearTab}
                  uiVariant="lovable"
                />
              ) : (
                <FilterSearchProduct
                  subgroups={sharedSubgroups[chosenTab]?.data}
                  products={shared[chosenTab]}
                  year={year ? year : yearTab}
                  uiVariant="lovable"
                />
              )}
            </div>

            {recentSubgroups.length > 0 && (
              <div className="pv2-recent-row">
                <span className="pv2-recent-label">
                  <Clock size={14} />
                  Recent:
                </span>
                <div className="pv2-recent-chips">
                  {recentSubgroups.map((sg) => (
                    <span key={sg.id} className="pv2-recent-chip">
                      {sg.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {productMenu === "service" && (
              <div className="pv2-content">
                <Service
                  chooseAlphabet={chooseAlphabet}
                  searchbar={searchbar}
                  chosenTab={chosenTab}
                />
              </div>
            )}

            {productMenu === "project" && (
              <div className="pv2-content pv2-project-placeholder">
                <p className="pv2-project-placeholder-text">{t("PROJECT")}</p>
                <p className="pv2-project-placeholder-hint">
                  Project portfolio view is coming soon.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </ContainerBody>
  );
};

export default ProductV2;
